import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaLinkedin } from 'react-icons/fa';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  
  const strapiUrl = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    // Fetch header data
    import('../services/api').then(({ fetchHeaderData }) => {
      fetchHeaderData().then(data => {
        if (data) setHeaderData(data);
      });
    });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  const defaultNavMenu = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about-us" },
    {
      name: "Services",
      dropdown: [
        { name: "Land Acquisition", path: "/services/land-acquisition" },
        { name: "Leasing", path: "/services/leasing" },
        { name: "Compliance", "path": "/services/compliance" },
        { name: "BTS Solutions", "path": "/services/bts-solutions" },
        { name: "Pre-Leased Assets", "path": "/services/pre-leased-assets" }
      ]
    },
    { name: "Projects", path: "/projects" },
    { name: "Blog", path: "/blog" }
  ];

  let navigationMenu = [...(headerData?.navigationMenu || defaultNavMenu)];
  
  // Ensure Projects is in the menu
  if (!navigationMenu.find(item => item.name === 'Projects' || item.name === 'Projects ')) {
    navigationMenu.push({ name: "Projects", path: "/projects" });
  }

  // Ensure Careers is in the menu
  if (!navigationMenu.find(item => item.name === 'Careers' || item.name === 'Careers ')) {
    navigationMenu.push({ name: "Careers", path: "/careers" });
  }
  const linkedinUrl = headerData?.linkedinUrl || "https://www.linkedin.com/company/pmg-land-leasing/";
  const contactText = headerData?.contactText || "Contact";
  
  const desktopLogoUrl = headerData?.logo?.url ? `${strapiUrl}${headerData.logo.url}` : "/PMGLOGO.png";
  const mobileLogoUrl = headerData?.logo?.url ? `${strapiUrl}${headerData.logo.url}` : "/PMGLOGO.png";

  return (
    <header className="header">
      <div className="container header-container">
        {/* Logo */}
        <div className="logo">
          <Link to="/" className="logo-link" aria-label="Go to Homepage">
            <img width="800" height="600"  
              src={desktopLogoUrl} 
              alt="PMG Land Aggregators" 
              className="logo-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/PMGLOGO.png";
              }}
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <ul className="nav-list">
            {navigationMenu.map((item, index) => {
              if (item.dropdown) {
                return (
                  <li key={index} className="dropdown nav-item" ref={dropdownRef}>
                    <button 
                      className={`dropdown-toggle ${isServicesOpen ? 'open' : ''}`} 
                      onClick={toggleServices}
                    >
                      {item.name}
                      <FaChevronDown className={`dropdown-icon ${isServicesOpen ? 'rotate' : ''}`} />
                    </button>
                    
                    {isServicesOpen && (
                      <div className="dropdown-wrapper">
                        <ul className="dropdown-menu">
                          {item.dropdown.map((subItem, subIndex) => (
                            <li key={subIndex} className="dropdown-item">
                              <Link 
                                to={subItem.path} 
                                onClick={closeMenu} 
                                className="dropdown-link"
                              >
                                {subItem.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              }
              
              return (
                <li key={index} className="nav-item">
                  <NavLink 
                    to={item.path} 
                    end={item.path === '/'} 
                    className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                  >
                    {item.name}
                  </NavLink>
                </li>
              );
            })}
            
            {/* SIMPLE CONTACT BUTTON - Desktop */}
            <li className="nav-item">
              <Link 
                to="/contact" 
                className="contact-btn"
              >
                {contactText}
              </Link>
            </li>
            <li className="nav-item">
              <a 
                href={linkedinUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="header-social-link"
                aria-label="LinkedIn"
                style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}
              >
                <FaLinkedin size={24} color="#0a66c2" />
              </a>
            </li>
          </ul>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </button>

        {/* Mobile Navigation */}
        <div className={`mobile-overlay ${isMenuOpen ? 'open' : ''}`} onClick={closeMenu}></div>
        <nav className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-header">
            <img width="800" height="600"  
              src={mobileLogoUrl} 
              alt="PMG Logo" 
              className="mobile-logo-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/PMGLOGO.png";
              }}
            />
          </div>
          
          <ul className="mobile-nav-list">
            {navigationMenu.map((item, index) => {
              if (item.dropdown) {
                return (
                  <li key={index} className="mobile-dropdown-item">
                    <button 
                      className="mobile-dropdown-toggle" 
                      onClick={toggleServices}
                    >
                      <span>{item.name}</span>
                      <FaChevronDown className={`mobile-dropdown-icon ${isServicesOpen ? 'rotate' : ''}`} />
                    </button>
                    
                    {isServicesOpen && (
                      <ul className="mobile-dropdown-menu">
                        {item.dropdown.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <Link 
                              to={subItem.path} 
                              onClick={closeMenu} 
                              className="mobile-dropdown-link"
                            >
                              {subItem.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li key={index} className="mobile-nav-item">
                  <Link to={item.path} onClick={closeMenu} className="mobile-nav-link">
                    {item.name}
                  </Link>
                </li>
              );
            })}
            
            {/* SIMPLE CONTACT BUTTON - Mobile */}
            <li className="mobile-nav-item mobile-contact-item">
              <Link 
                to="/contact" 
                onClick={closeMenu} 
                className="mobile-contact-btn"
              >
                {contactText}
              </Link>
            </li>
            <li className="mobile-nav-item" style={{ marginTop: '15px', display: 'flex', justifyContent: 'center' }}>
              <a 
                href={linkedinUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="mobile-social-link"
                aria-label="LinkedIn"
              >
                <FaLinkedin size={32} color="#0a66c2" />
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;